import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner } from 'react-bootstrap';
import { setPending } from '../redux/slices/djLibraryChangesSlice';
import { track } from '../utils/analytics';
import './DjChangeBanner.scss';

const n = (c, one, many) => `${c} ${c === 1 ? one : many}`;

/**
 * Flatten the watcher diff into display rows: a headline per change, with the
 * track names the watcher carried (capped at the source) nested under it.
 */
function detailRows(diff) {
    if (!diff) return [];
    const rows = [];
    const names = (list, total) => {
        const out = (list || []).map(t => t.label);
        // "+7 more" under zero names is nonsense — the headline already
        // carries the count, so an unnamed change gets no sub-list at all.
        if (out.length && total > out.length) out.push(`+${total - out.length} more`);
        return out;
    };
    if (diff.libraryTracksAdded) rows.push({ text: `${n(diff.libraryTracksAdded, 'track', 'tracks')} added to library`, sub: names(diff.libraryTracks, diff.libraryTracksAdded) });
    for (const p of diff.newPlaylists || []) rows.push({ text: `New playlist "${typeof p === 'string' ? p : p.name}"` });
    for (const t of diff.tracksAdded || []) rows.push({ text: `${n(t.count, 'track', 'tracks')} added to "${t.playlist}"`, sub: names(t.tracks, t.count) });
    for (const r of diff.renamed || []) rows.push({ text: `"${r.from}" renamed to "${r.to}"` });
    return rows;
}

/**
 * Removals are reported but never synced (MixGoat doesn't delete from your
 * library), so they're a footnote — not a peer of the actionable lines.
 */
function removalNote(diff, label) {
    if (!diff) return null;
    const tracks = (diff.tracksRemoved || []).reduce((s, t) => s + t.count, 0);
    const playlists = (diff.removedPlaylists || []).length;
    if (!tracks && !playlists) return null;
    const parts = [];
    if (tracks) parts.push(n(tracks, 'track', 'tracks'));
    if (playlists) parts.push(n(playlists, 'playlist', 'playlists'));
    return `${parts.join(' and ')} removed in ${label} — not synced, MixGoat never deletes from your library`;
}

/**
 * Un-synced-changes strip at the top of a DJ-app sidebar while that app has
 * changes MixGoat hasn't pulled in yet. Reads the pending diff from redux;
 * the sidebar passes its own import handler. Summary on top (full width),
 * actions below; "Details" expands to one line per change.
 */
export default function DjChangeBanner({ app, onSync, syncing = false }) {
    const dispatch = useDispatch();
    const change = useSelector((s) => s.djLibraryChanges?.pending?.[app]);
    const [open, setOpen] = useState(false);
    if (!change) return null;

    const rows = detailRows(change.diff);
    const note = removalNote(change.diff, change.label);
    const count = rows.length + (note ? 1 : 0);

    const dismiss = async () => {
        track('dj_library_change_dismissed', { app });
        dispatch(setPending({ app, change: null }));
        try { await window.electron?.djlibrary?.dismiss(app); } catch { /* best-effort */ }
    };

    const toggleDetails = () => {
        if (!open) track('dj_library_change_details_opened', { app, changes: count });
        setOpen(!open);
    };

    return (
        <div className={`dj-change-banner${open ? ' is-open' : ''}`} role="status">
            <div className="dj-change-head">
                <span className="dj-change-dot" aria-hidden="true" />
                {/* No "Updates from <App>" title — this banner lives inside that app's own sidebar. */}
                <div className="dj-change-summary" title={`Updates from ${change.label} — ${change.summary}`}>{change.summary}</div>
                <button type="button" className="dj-change-dismiss" onClick={dismiss} title="Dismiss" aria-label="Dismiss">×</button>
            </div>
            <div className="dj-change-actions">
                <Button size="sm" className="dj-change-sync" disabled={syncing} onClick={() => { track('dj_library_change_sync_clicked', { app, from: 'sidebar_banner' }); onSync?.(); }}>
                    {syncing ? <Spinner animation="border" size="sm" /> : 'Sync now'}
                </Button>
                <button type="button" className="dj-change-toggle" onClick={toggleDetails} aria-expanded={open}>
                    {open ? 'Hide details' : `Details (${count})`}
                    <span className="dj-change-chevron" aria-hidden="true" />
                </button>
            </div>
            {open && (
                <ul className="dj-change-details">
                    {rows.map((r, i) => (
                        <li key={i} title={r.text}>
                            {r.text}
                            {r.sub?.length > 0 && (
                                <ul className="dj-change-tracks">
                                    {r.sub.map((t, j) => <li key={j} title={t}>{t}</li>)}
                                </ul>
                            )}
                        </li>
                    ))}
                    {note && <li className="dj-change-note" title={note}>{note}</li>}
                </ul>
            )}
        </div>
    );
}
